// src/components/Shared/FloatingNav.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { playVoice } from '../../utils/voicePlayer';

// 9 Модулей в точности по спецификации page.html и DESIGN_OVERLOAD_ANTIPATTERNS.md
const NAV_MODULES = [
  {
    id: 'home',
    labelKey: 'home',
    defaultLabel: 'Главная',
    path: '/',
    exact: true,
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    )
  },
  {
    id: 'rcon',
    labelKey: 'rconHacking',
    defaultLabel: 'RCON Аудит',
    path: '/rcon-audit',
    voiceKey: 'rcon-scan',
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 17 10 11 4 5" />
        <line x1="12" y1="19" x2="20" y2="19" />
      </svg>
    )
  },
  {
    id: 'data',
    labelKey: 'serverDataExtraction',
    defaultLabel: 'Извлечение Данных',
    path: '/server-data-extraction',
    voiceKey: 'data-extraction',
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
      </svg>
    )
  },
  {
    id: 'acc',
    labelKey: 'accountChecker',
    defaultLabel: 'Аккаунты',
    path: '/account-checker',
    voiceKey: 'account-checker',
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <polyline points="16 11 18 13 22 9" />
      </svg>
    )
  },
  {
    id: 'bots',
    labelKey: 'bots',
    defaultLabel: 'Боты',
    path: '/bots',
    voiceKey: 'bots',
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
        <rect width="16" height="12" x="4" y="8" rx="2" />
        <path d="M2 14h2" />
        <path d="M20 14h2" />
        <path d="M15 13v2" />
        <path d="M9 13v2" />
      </svg>
    )
  },
  {
    id: 'ddos',
    labelKey: 'ddosTesting',
    defaultLabel: 'DDoS Тест',
    path: '/ddos',
    voiceKey: 'ddos-testing',
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="m13 2-2 2.5h3L11 9l4.5-1.5L13 12h3l-5 10 2-7.5H10L13 2z" />
      </svg>
    )
  },
  {
    id: 'servers',
    labelKey: 'servers',
    defaultLabel: 'Серверы',
    path: '/servers',
    voiceKey: 'server-database',
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect width="20" height="8" x="2" y="2" rx="2" ry="2" />
        <rect width="20" height="8" x="2" y="14" rx="2" ry="2" />
        <line x1="6" y1="6" x2="6.01" y2="6" />
        <line x1="6" y1="18" x2="6.01" y2="18" />
      </svg>
    )
  },
  {
    id: 'parser',
    labelKey: 'parserShort',
    defaultLabel: 'Парсер',
    path: '/monitoring-parser',
    voiceKey: 'servers-parser',
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="m4.93 4.93 4.24 4.24" />
        <path d="m14.83 9.17 4.24-4.24" />
        <path d="m14.83 14.83 4.24 4.24" />
        <path d="m9.17 14.83-4.24 4.24" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    )
  },
  {
    id: 'zeroday',
    labelKey: 'secret',
    defaultLabel: 'Zero-Day',
    path: '/secret',
    voiceKey: 'zeroday-method',
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="22" x2="18" y1="12" y2="12" />
        <line x1="6" x2="2" y1="12" y2="12" />
        <line x1="12" x2="12" y1="6" y2="2" />
        <line x1="12" x2="12" y1="22" y2="18" />
      </svg>
    )
  }
];

const MODULE_THEMES = {
  home: {
    bg: '#ffffff',
    text: '#000000',
    shadow: 'rgba(255, 255, 255, 0.4)',
    hoverBg: '#f4f4f5',
    brandBg: '#ffffff',
    brandText: '#000000',
    brandShadow: 'rgba(255, 255, 255, 0.4)'
  },
  rcon: {
    bg: '#c026d3',
    text: '#000000',
    shadow: 'rgba(192, 38, 211, 0.45)',
    hoverBg: '#d946ef',
    brandBg: '#c026d3',
    brandText: '#000000',
    brandShadow: 'rgba(192, 38, 211, 0.45)'
  },
  data: {
    bg: '#14b8a6',
    text: '#000000',
    shadow: 'rgba(20, 184, 166, 0.45)',
    hoverBg: '#2dd4bf',
    brandBg: '#14b8a6',
    brandText: '#000000',
    brandShadow: 'rgba(20, 184, 166, 0.45)'
  },
  acc: {
    bg: '#1bd96a',
    text: '#000000',
    shadow: 'rgba(27, 217, 106, 0.45)',
    hoverBg: '#22c55e',
    brandBg: '#1bd96a',
    brandText: '#000000',
    brandShadow: 'rgba(27, 217, 106, 0.45)'
  },
  bots: {
    bg: '#3b82f6',
    text: '#000000',
    shadow: 'rgba(59, 130, 246, 0.45)',
    hoverBg: '#60a5fa',
    brandBg: '#3b82f6',
    brandText: '#000000',
    brandShadow: 'rgba(59, 130, 246, 0.45)'
  },
  ddos: {
    bg: '#ef4444',
    text: '#000000',
    shadow: 'rgba(239, 68, 68, 0.45)',
    hoverBg: '#f87171',
    brandBg: '#ef4444',
    brandText: '#000000',
    brandShadow: 'rgba(239, 68, 68, 0.45)'
  },
  servers: {
    bg: '#0ea5e9',
    text: '#000000',
    shadow: 'rgba(14, 165, 233, 0.45)',
    hoverBg: '#38bdf8',
    brandBg: '#0ea5e9',
    brandText: '#000000',
    brandShadow: 'rgba(14, 165, 233, 0.45)'
  },
  parser: {
    bg: '#f97316',
    text: '#000000',
    shadow: 'rgba(249, 115, 22, 0.45)',
    hoverBg: '#fb923c',
    brandBg: '#f97316',
    brandText: '#000000',
    brandShadow: 'rgba(249, 115, 22, 0.45)'
  },
  zeroday: {
    bg: '#ffffff',
    text: '#000000',
    shadow: 'rgba(255, 255, 255, 0.4)',
    hoverBg: '#f4f4f5',
    brandBg: '#ffffff',
    brandText: '#000000',
    brandShadow: 'rgba(255, 255, 255, 0.4)'
  },
  account: {
    bg: '#ffffff',
    text: '#000000',
    shadow: 'rgba(255, 255, 255, 0.4)',
    hoverBg: '#f4f4f5',
    brandBg: '#ffffff',
    brandText: '#000000',
    brandShadow: 'rgba(255, 255, 255, 0.4)'
  }
};

const DEFAULT_THEME = {
  bg: '#c026d3',
  text: '#000000',
  shadow: 'rgba(192, 38, 211, 0.45)',
  hoverBg: '#d946ef',
  brandBg: '#c026d3',
  brandText: '#000000',
  brandShadow: 'rgba(192, 38, 211, 0.45)'
};

const FloatingNav = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const { isAuthenticated, user, logout, triggerGoogleLogin, isLoggingIn } = useAuth();
  const avatarUrl =
    user?.avatar ||
    user?.picture ||
    (typeof window !== 'undefined' ? localStorage.getItem('google_avatar') : null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const toggleMobile = useCallback(() => setMobileOpen(v => !v), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileOpen]);

  const currentPath = location.pathname;
  const isEn = currentPath.startsWith('/en');
  const cleanPath = isEn ? currentPath.replace(/^\/en/, '') || '/' : currentPath;
  const isAccountPage = cleanPath === '/account' || cleanPath.startsWith('/account/');

  const isModuleActive = (item) => {
    if (item.id === 'parser') {
      return cleanPath === '/monitoring-parser' || cleanPath === '/servers-parser';
    }
    if (item.id === 'bots') {
      return cleanPath === '/bots';
    }
    if (item.exact) {
      return cleanPath === item.path;
    }
    return cleanPath.startsWith(item.path);
  };

  const activeModule = NAV_MODULES.find(mod => isModuleActive(mod));
  const currentTheme = isAccountPage
    ? MODULE_THEMES.account
    : (activeModule ? (MODULE_THEMES[activeModule.id] || DEFAULT_THEME) : DEFAULT_THEME);

  return (
    <>
      {/* ═══════ DESKTOP DOCK (Apple / Grok Aesthetic) ═══════ */}
      <header className="fixed top-4 left-1/2 -translate-x-1/2 z-50 hidden md:flex items-center justify-center max-w-[calc(100vw-32px)]">
        <nav
          className="flex items-center gap-1.5 p-1.5 rounded-full border border-white/[0.18] shadow-[0_20px_40px_-10px_rgba(0,0,0,0.95)]"
          style={{
            background: 'rgba(12, 12, 16, 0.88)',
            backdropFilter: 'blur(30px) saturate(190%)',
            WebkitBackdropFilter: 'blur(30px) saturate(190%)',
            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.95), inset 0 1px 1px rgba(255, 255, 255, 0.25)'
          }}
        >
          {/* Круглый сплошной неоновый значок с молнией */}
          <Link
            to="/"
            className="w-[38px] h-[38px] rounded-full flex items-center justify-center shrink-0 transition-all duration-300 hover:scale-[1.08] active:scale-95"
            style={{
              backgroundColor: currentTheme.brandBg,
              color: currentTheme.brandText,
              boxShadow: `0 2px 14px ${currentTheme.brandShadow}`,
              transition: 'background-color 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            title="StormBlack Core"
          >
            <svg viewBox="0 0 24 24" className="w-[16px] h-[16px]" style={{ fill: currentTheme.brandText, transition: 'fill 0.35s ease' }}>
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </Link>

          {/* 9 Модулей */}
          <div className="flex items-center gap-1">
            {NAV_MODULES.map((mod) => {
              const active = isModuleActive(mod);
              const label = t(mod.labelKey) || mod.defaultLabel;
              const theme = MODULE_THEMES[mod.id] || DEFAULT_THEME;

              return (
                <Link
                  key={mod.id}
                  to={mod.path}
                  onClick={() => {
                    if (mod.voiceKey) playVoice(mod.voiceKey);
                  }}
                  className={`group relative h-9 rounded-full inline-flex items-center justify-center text-[13px] select-none whitespace-nowrap ${
                    active
                      ? 'px-3.5 font-bold'
                      : 'text-white/70 hover:text-white hover:bg-white/[0.08] px-2.5 font-medium'
                  }`}
                  style={{
                    transition: 'padding 0.28s cubic-bezier(0.16, 1, 0.3, 1)'
                  }}
                >
                  {/* Скользящая неоновая плашка активного окна */}
                  {active && (
                    <motion.div
                      layoutId="activeNavIndicator"
                      className="absolute inset-0 rounded-full z-0 pointer-events-none"
                      style={{
                        backgroundColor: theme.bg,
                        boxShadow: `0 2px 14px ${theme.shadow}`
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 350,
                        damping: 30,
                        mass: 0.8
                      }}
                    />
                  )}

                  <span
                    className="relative z-10 flex items-center justify-center transition-colors duration-200"
                    style={active ? { color: theme.text } : {}}
                  >
                    {mod.icon}
                  </span>

                  <AnimatePresence initial={false}>
                    {active && (
                      <motion.span
                        initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                        animate={{ opacity: 1, width: 'auto', marginLeft: 7 }}
                        exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                        transition={{
                          duration: 0.28,
                          ease: [0.16, 1, 0.3, 1]
                        }}
                        className="relative z-10 tracking-tight text-[13px] font-bold overflow-hidden whitespace-nowrap"
                        style={{ color: theme.text }}
                      >
                        {label}
                      </motion.span>
                    )}
                  </AnimatePresence>

                  {!active && (
                    <span className="pointer-events-none absolute -bottom-9 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full text-[12px] font-semibold text-white whitespace-nowrap bg-[#111116] border border-white/20 shadow-[0_10px_20px_rgba(0,0,0,0.8)] opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all duration-150 ease-out z-[200]">
                      {label}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          <div className="w-[1px] h-[22px] bg-white/[0.18] mx-1 shrink-0" />

          {/* Кнопка входа / Профиль */}
          {isAuthenticated && user ? (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(v => !v)}
                className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full transition-all duration-200 ${
                  isAccountPage
                    ? 'bg-white text-black shadow-[0_2px_12px_rgba(255,255,255,0.35)]'
                    : 'hover:bg-white/[0.08]'
                }`}
              >
                <div className={`w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-[12px] font-bold shrink-0 ${
                  isAccountPage ? 'bg-black text-white' : 'bg-white text-black'
                }`}>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={user.name || user.email || 'User'}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    (user.name || user.email)?.[0]?.toUpperCase() || 'U'
                  )}
                </div>
                <svg
                  viewBox="0 0 24 24"
                  className={`w-3 h-3 fill-none transition-transform duration-200 ${
                    isAccountPage ? 'stroke-black/70' : 'stroke-white/60'
                  } ${profileOpen ? 'rotate-180' : ''}`}
                  strokeWidth="2.5"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              <AnimatePresence>
                {profileOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: -6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.94, y: -6 }}
                    transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                    style={{ transformOrigin: 'top right' }}
                    className="absolute top-full right-0 mt-3 w-60 bg-[#0c0c10] border border-white/[0.18] rounded-2xl shadow-2xl overflow-hidden p-1.5 backdrop-blur-2xl z-[100]"
                  >
                    <div className="px-3.5 py-2.5 border-b border-white/10 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-white/10 border border-white/15 flex items-center justify-center text-xs font-bold text-white shrink-0">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={user.name || user.email || 'User'}
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          (user.name || user.email)?.[0]?.toUpperCase() || 'U'
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white truncate" title={user.email || user.name}>
                          {user.email || user.name || 'Пользователь'}
                        </div>
                      </div>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/account"
                        className={`flex items-center gap-2.5 px-3.5 py-2 text-sm rounded-xl transition-colors ${
                          isAccountPage
                            ? 'bg-white text-black font-semibold shadow-[0_2px_10px_rgba(255,255,255,0.35)]'
                            : 'text-white/80 hover:text-white hover:bg-white/[0.08]'
                        }`}
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none" strokeWidth="2">
                          <circle cx="12" cy="7" r="4" /><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        </svg>
                        Аккаунт
                      </Link>
                      <button
                        onClick={logout}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-colors"
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none" strokeWidth="2">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        Выйти
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <button
              onClick={() => !isLoggingIn && triggerGoogleLogin()}
              disabled={isLoggingIn}
              className={`h-[38px] px-5 rounded-full text-[13px] font-bold transition-all duration-300 hover:scale-[1.03] active:scale-95 shrink-0 flex items-center justify-center gap-2 ${
                isLoggingIn ? 'opacity-85 pointer-events-none' : ''
              }`}
              style={{
                backgroundColor: currentTheme.brandBg,
                color: currentTheme.brandText,
                boxShadow: `0 4px 16px ${currentTheme.brandShadow}`,
                transition: 'background-color 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1), color 0.35s ease'
              }}
            >
              {isLoggingIn ? (
                <>
                  <span
                    className="w-3.5 h-3.5 border-2 rounded-full animate-spin shrink-0"
                    style={{
                      borderColor: 'rgba(0, 0, 0, 0.25)',
                      borderTopColor: currentTheme.brandText
                    }}
                  />
                  <span>{t('loggingIn') || 'Вход...'}</span>
                </>
              ) : (
                t('login') || 'Войти'
              )}
            </button>
          )}
        </nav>
      </header>

      {/* ═══════ MOBILE NAV ═══════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 md:hidden bg-black/90 backdrop-blur-xl border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" onClick={closeMobile}>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
              style={{
                backgroundColor: currentTheme.brandBg,
                boxShadow: `0 2px 10px ${currentTheme.brandShadow}`
              }}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" style={{ fill: currentTheme.brandText }}>
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            </div>
            <span className="font-display text-xl tracking-wider text-white">STORMBLACK</span>
          </Link>

          <div className="flex items-center gap-2">
            {!isAuthenticated ? (
              <button
                onClick={() => !isLoggingIn && triggerGoogleLogin()}
                disabled={isLoggingIn}
                className={`px-4 py-1.5 rounded-full text-xs font-bold active:scale-95 transition-all flex items-center gap-1.5 ${
                  isLoggingIn ? 'opacity-85 pointer-events-none' : ''
                }`}
                style={{
                  backgroundColor: currentTheme.brandBg,
                  color: currentTheme.brandText,
                  boxShadow: `0 2px 10px ${currentTheme.brandShadow}`
                }}
              >
                {isLoggingIn ? (
                  <>
                    <span
                      className="w-3 h-3 border-2 rounded-full animate-spin shrink-0"
                      style={{
                        borderColor: 'rgba(0, 0, 0, 0.25)',
                        borderTopColor: currentTheme.brandText
                      }}
                    />
                    <span>{t('loggingIn') || 'Вход...'}</span>
                  </>
                ) : (
                  t('login') || 'Войти'
                )}
              </button>
            ) : (
              <Link
                to="/account"
                className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-xs font-bold transition-colors shrink-0"
                style={{
                  backgroundColor: currentTheme.brandBg,
                  color: currentTheme.brandText,
                  boxShadow: `0 2px 10px ${currentTheme.brandShadow}`
                }}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={user.name || user.email || 'User'}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  (user.name || user.email)?.[0]?.toUpperCase() || 'U'
                )}
              </Link>
            )}

            <button
              onClick={toggleMobile}
              className="w-9 h-9 rounded-full bg-white/[0.08] flex items-center justify-center text-white"
              aria-label="Меню"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none" strokeWidth="2.5">
                {mobileOpen ? (
                  <path d="M18 6 6 18M6 6l12 12" />
                ) : (
                  <path d="M4 12h16M4 6h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-3 pt-3 border-t border-white/10 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
                {NAV_MODULES.map((mod) => {
                  const active = isModuleActive(mod);
                  const label = t(mod.labelKey) || mod.defaultLabel;
                  const theme = MODULE_THEMES[mod.id] || DEFAULT_THEME;

                  return (
                    <Link
                      key={mod.id}
                      to={mod.path}
                      onClick={() => {
                        closeMobile();
                        if (mod.voiceKey) playVoice(mod.voiceKey);
                      }}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        active
                          ? 'font-bold'
                          : 'text-white/70 hover:text-white hover:bg-white/[0.08]'
                      }`}
                      style={active ? {
                        backgroundColor: theme.bg,
                        color: theme.text,
                        boxShadow: `0 2px 10px ${theme.shadow}`
                      } : {}}
                    >
                      <span style={active ? { color: theme.text } : {}}>{mod.icon}</span>
                      <span style={active ? { color: theme.text } : {}}>{label}</span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Spacer for desktop & mobile */}
      <div className="h-16 md:h-20 shrink-0" />
    </>
  );
};

export default FloatingNav;